import { LiveIndicator }   from '@/components/ui/LiveIndicator';
import { CortexLogoFull }  from '@/components/ui/CortexLogo';
import { CortexLoadingScreen } from '@/components/ui/CortexLoadingScreen';
import { formatClock }     from '@/lib/formatters';
import { RefreshCw, LogOut, X } from 'lucide-react';
import { Link }            from 'react-router-dom';
import { cn }              from '@/lib/cn';
import { useAuth }         from '@/auth/AuthContext';
import { useState }        from 'react';
import { ThemeToggle }     from '@/components/Theme';
import { LanguageSwitcher } from '@/components/Language';
import { useLanguage }     from '@/hooks/useLanguage';

interface HeaderProps {
  siteName:   string;
  /** Timestamp of the LAST reading in the sheet (frozen at that time). */
  lastDataAt: Date | null;
  refreshing: boolean;
  onRefresh:  () => void;
}

export function Header({ siteName, lastDataAt, refreshing, onRefresh }: HeaderProps) {
  const { signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [showLoadingPreview, setShowLoadingPreview] = useState(false);
  const { t } = useLanguage();

  // Show the timestamp of the LAST row in the sheet — frozen at that moment.
  const displayTime = lastDataAt
    ? `${formatClock(lastDataAt)} IST`
    : t('— · awaiting data');

  async function handleSignOut() {
    setSigningOut(true);
    await signOut();
  }

  return (
    <header className="relative pad-safe-top">
      <div className="absolute inset-0 blob -z-10" />

      {/* Loading Animation Overlay Preview */}
      {showLoadingPreview && (
        <div className="fixed inset-0 z-50">
          <CortexLoadingScreen fullScreen={true} title="Previewing Cortex Loading System..." />
          <button
            onClick={() => setShowLoadingPreview(false)}
            aria-label={t("Close Preview")}
            className="fixed top-4 right-4 z-50 flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-ink-900 text-white text-xs font-semibold shadow-lg hover:bg-ink-800 active:scale-95 transition"
          >
            <X className="w-4 h-4" />
            <span>{t("Close Preview")}</span>
          </button>
        </div>
      )}

      <div className="px-4 pb-3 pt-1">

        {/* ── Logo row ── */}
        <div className="relative flex items-center justify-center min-h-[56px]">

          {/* LEFT: Logout button */}
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            aria-label={t('Sign out')}
            className={cn(
              'absolute left-0 w-9 h-9 grid place-items-center rounded-full',
              'bg-ink-100/80 border border-ink-200/70 text-ink-600',
              'active:scale-95 transition shadow-card',
              signingOut && 'opacity-60',
            )}
          >
            <LogOut className="w-4 h-4" />
          </button>

          {/* CENTER: Logo + site name */}
          <Link
            to="/"
            className="flex flex-col items-center gap-1 active:opacity-70 transition-opacity"
            aria-label={t("Dashboard")}
          >
            <CortexLogoFull height={54} className="text-ink-900" />
            <span className="text-[17px] font-black tracking-tight text-ink-900 leading-none">
              {t(siteName)}
            </span>
          </Link>

          {/* RIGHT: Floating Buttons Stack (Refresh, Theme, Language) */}
          <div className="absolute right-0 top-[10px] flex flex-col gap-3.5 items-center z-40">
            <button
              onClick={onRefresh}
              disabled={refreshing}
              aria-label={t("Refresh")}
              className={cn(
                'w-9 h-9 grid place-items-center rounded-full',
                'bg-ink-100/80 border border-ink-200/70 text-ink-600',
                'active:scale-95 transition shadow-card',
                'floating-action-btn',
                refreshing && 'opacity-60',
              )}
            >
              <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
            </button>
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </div>



        {/* Meter switching now lives in the "Meter overview" pills (MeterPicker). */}

        {/* ── Last-data · live row ── */}
        <div className="mt-2 flex items-center justify-center gap-0">
          <span className="text-[12px] font-semibold text-ink-700">
            {displayTime}
          </span>
          <span className="mx-2 text-ink-300 font-bold">·</span>
          <LiveIndicator lastDataAt={lastDataAt} />
        </div>
      </div>
    </header>
  );
}